import React, { useState, useEffect, useRef } from 'react';
import { serverPHP } from '../const/consts';
import SyntheseSession from './SyntheseSession';
import { Document, Packer, Paragraph, TextRun } from 'docx';
import { saveAs } from 'file-saver';

const ResultatFinal = ({ 
  user, 
  sujet, 
  currentPhase, 
  setModalContent, 
  setModalTitle, 
  setShowModal, 
  parametres, 
  type, 
  redactionLongue 
}) => {
  const [pastContributions, setPastContributions] = useState([]);
  const [copied, setCopied] = useState(false);
  const planRef = useRef(null);

  useEffect(() => {
    fetch(`${serverPHP}getContributions.php`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        session_id: user.currentsession,
        user_id: user.id,
        phase: currentPhase,
        action: 'final',
      }),
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.past_contributions) {
          setPastContributions(data.past_contributions);
        }
      })
      .catch((error) => console.error('Failed to fetch contributions:', error));
  }, [currentPhase, user.currentsession, user.id]);

  const exportToDocx = async () => {
    if (!planRef.current) return;
    
    try {
      const paragraphs = [];

      paragraphs.push(
        new Paragraph({
          children: [
            new TextRun({
              text: `Sujet : ${sujet}`,
              bold: true,
              size: 28,
            }),
          ],
          spacing: {
            after: 400,
          },
        }),
        new Paragraph({
          children: [
            new TextRun({
              text: '',
            }),
          ],
          spacing: {
            after: 200,
          },
        })
      );

      const lines = planRef.current.querySelectorAll('.line');
      lines.forEach((line) => {
        const text = line.innerText;
        const indentLevel = parseInt(line.style.marginLeft) > 0 ? 1 : 0; // Adaptation au nouveau système

        paragraphs.push(
          new Paragraph({
            children: [
              new TextRun({
                text: text,
                size: 24,
              }),
            ],
            indent: {
              left: indentLevel * 720,
            },
            spacing: {
              after: 120,
            },
          })
        );
      });

      const doc = new Document({
        sections: [
          {
            children: paragraphs,
          },
        ],
      });

      const blob = await Packer.toBlob(doc);
      saveAs(blob, `session-${user.currentsession}-resultat.docx`);
      
      // UI optimiste - pas de feedback de chargement, juste succès
      setModalContent(<div className="text-center">✅ Document Word exporté avec succès !</div>);
      setModalTitle("🎉 Export réussi");
      setShowModal(true);
    } catch (error) {
      console.error('Failed to generate DOCX file:', error);
      setModalContent(<div className="text-center">❌ Erreur lors de l'export du document.</div>);
      setModalTitle("🚫 Erreur d'export");
      setShowModal(true);
    }
  };

  const copyToClipboard = async () => {
    if (!planRef.current) return;
    
    try {
      let textToCopy = `Sujet : ${sujet}\n\n`;

      const lines = planRef.current.querySelectorAll('.line');
      lines.forEach((line) => {
        const indentLevel = parseInt(line.style.marginLeft) > 0 ? 1 : 0; // Adaptation au nouveau système
        const text = line.innerText;
        textToCopy += `${'\t'.repeat(indentLevel)}${text}\n`;
      });

      await navigator.clipboard.writeText(textToCopy);
      
      // UI optimiste - affichage immédiat du succès
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy text:', err);
      
      // Fallback pour les navigateurs plus anciens
      try {
        const textArea = document.createElement('textarea');
        let textToCopy = `Sujet : ${sujet}\n\n`;
        
        const lines = planRef.current.querySelectorAll('.line');
        lines.forEach((line) => {
          const indentLevel = parseInt(line.style.marginLeft) > 0 ? 1 : 0;
          const text = line.innerText;
          textToCopy += `${'\t'.repeat(indentLevel)}${text}\n`;
        });
        
        textArea.value = textToCopy;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (fallbackErr) {
        console.error('Fallback copy also failed:', fallbackErr);
        setModalContent(<div className="text-center">❌ Impossible de copier dans le presse-papiers. Veuillez sélectionner et copier manuellement le texte.</div>);
        setModalTitle("🚫 Erreur de copie");
        setShowModal(true);
      }
    }
  };

  return (
    <div className="fade-in">
      {/* Header avec actions */}
      <div className="card">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-primary mb-2">🎉 Résultat final</h1>
            <p className="text-secondary">Session {user.currentsession} terminée avec succès</p>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-2">
            <button
              onClick={copyToClipboard}
              className={`btn ${copied ? 'btn-success' : 'btn-secondary'}`}
            >
              {copied ? (
                <>
                  <span>✅ Copié !</span>
                </>
              ) : (
                <>
                  <span>📋</span>
                  <span>Copier</span>
                </>
              )}
            </button>
            
            <button
              onClick={exportToDocx}
              className="btn btn-primary"
            >
              <span>📄</span>
              <span>Export Word</span>
            </button>
          </div>
        </div>

        {/* Avertissement */}
        <div className=" mb-6" style={{ background: 'var(--warning-color)', color: 'white',
          padding:'1rem', borderRadius:'0.5rem'
         }}>
          <div className="flex items-center gap-3">
            <span className="text-2xl icon-temporary">⏰</span>
            <div>
              <h4 className="font-semibold mb-1">Session temporaire</h4>
              <p className="text-sm opacity-90">
                Les sessions terminées sont automatiquement supprimées après 48 heures. 
                Pensez à sauvegarder vos résultats !
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Résultat final */}
      <div className="card" ref={planRef}>
        <h2 className="card-title">
          {type === 'plan' ? '📊 Plan final' : '📋 Synthèse finale'}
        </h2>
        
        <SyntheseSession
          sujet={sujet}
          premieres_contributions={pastContributions}
          setModalContent={setModalContent}
          setModalTitle={setModalTitle}
          setShowModal={setShowModal}
          parametres={parametres}
          type={type}
          redactionLongue={false}
          redactionLonguePourTop={redactionLongue}
          resultatFinal={true}
        />
      </div>

      {/* Statistiques */}
      <div className="card">
        <h3 className="card-title">📊 Statistiques de la session</h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="text-center p-4 bg-secondary rounded-lg">
            <div className="text-2xl mb-2">📝</div>
            <div className="font-semibold text-lg">{pastContributions.length}</div>
            <div className="text-sm text-secondary">
              {type === 'plan' ? 'Éléments du plan' : 'Phases complétées'}
            </div>
          </div>
          
          <div className="text-center p-4 bg-secondary rounded-lg">
            <div className="text-2xl mb-2">🏆</div>
            <div className="font-semibold text-lg">
              {pastContributions.reduce((total, phase) => total + phase.contributions.length, 0)}
            </div>
            <div className="text-sm text-secondary">Contributions totales</div>
          </div>
          
          <div className="text-center p-4 bg-secondary rounded-lg">
            <div className="text-2xl mb-2">🎯</div>
            <div className="font-semibold text-lg">100%</div>
            <div className="text-sm text-secondary">Session terminée</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResultatFinal;